/* eslint-disable factorial/no-raw-html */
import { useState } from 'react'

import { Box, colors } from '@factorialco/deprecated-design-system'
import { CartesianMarkerProps } from '@nivo/core'
import { ResponsiveLine } from '@nivo/line'
import { compact } from 'lodash'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import EmptyState from 'modules/project_management/components/graphs/EmptyState'
import { overviewFrequencyOptionsEnum } from 'modules/project_management/lib/constants'
import { dynamicFiltering } from 'modules/project_management/lib/overviewOptions'

import EvolutionGraphLoadingSkeleton from '../../graphs/EvolutionGraph/EvolutionGraphLoadingSkeleton'
import {
  ConstantTransformedData,
  EvolutionTransformedData,
} from '../EvolutionChart/getEvolutionTransformedData'
import Legend, { LegendItem } from '../Legend'
import { ResponsiveChartProps } from '../types/responsiveChartProps'

interface CartesianMarkerPropsWithId extends CartesianMarkerProps {
  id: string
}

interface ResponsiveLineChartProps {
  evolutionData: EvolutionTransformedData
  constantData?: ConstantTransformedData[]
  unit: string
  formatYAxis?: (value: number) => string
  onChangeEvolutionDataShown?: (isShown: boolean) => void
  selectedFrequency: overviewFrequencyOptionsEnum
}

export const ResponsiveLineChart: React.FC<ResponsiveChartProps<ResponsiveLineChartProps>> = (
  props
) => {
  const {
    evolutionData,
    constantData = [],
    unit,
    formatYAxis = undefined,
    onChangeEvolutionDataShown,
    selectedFrequency,
  } = props
  const chartIds = constantData.map<string>((c) => c.id).concat(evolutionData.id)

  const legendItems = constantData
    .map<LegendItem>((c) => {
      return { id: c.id, label: c.label, color: c.color }
    })
    .concat({
      id: evolutionData.id,
      label: evolutionData.label,
      color: evolutionData.color,
    })

  const [activeSeries, setActiveSeries] = useState<string[]>(chartIds)

  const manageChartVisibility = (id: string) => {
    if (activeSeries.length === 1 && activeSeries.includes(id)) return
    if (activeSeries.includes(id)) {
      setActiveSeries(activeSeries.filter((i) => i !== id))

      if (id === evolutionData.id) {
        onChangeEvolutionDataShown && onChangeEvolutionDataShown(false)
      }
    } else {
      setActiveSeries([...activeSeries, id])

      if (id === evolutionData.id) {
        onChangeEvolutionDataShown && onChangeEvolutionDataShown(true)
      }
    }
  }

  const markers = constantData.map<CartesianMarkerPropsWithId>((constant) => {
    return {
      id: constant.id,
      axis: 'y',
      legendPosition: 'bottom-left',
      value: constant.value,
      legend: `${constant.label}: ${constant.value}${unit}`,
      textStyle: {
        fontSize: 12,
        fontWeight: 600,
      },
    }
  })

  const filteredEvolutionData = activeSeries.includes(evolutionData.id) ? evolutionData : null
  const filteredConstantData = constantData.filter((constant) => activeSeries.includes(constant.id))
  const filteredMarkers = markers.filter((marker) => activeSeries.includes(marker.id))

  const convertedConstantData = filteredConstantData.map((cd) => {
    return {
      id: cd.id,
      label: cd.label,
      max: cd.value,
      min: cd.value,
      data: evolutionData.data.map((item, index) => {
        return {
          x: index,
          y: cd.value,
          title: item.title,
        }
      }),
      color: cd.color,
    }
  })

  const flatData = evolutionData.max === evolutionData.min

  const minY = () => (flatData ? 0 : 'auto')

  const maxY = () =>
    Math.max(
      filteredEvolutionData ? filteredEvolutionData.max : 0,
      ...filteredMarkers.map((c) => Number(c.value))
    ) == 0
      ? 4
      : 'auto'

  const shownData = compact([...convertedConstantData, filteredEvolutionData ?? null])
  const shownColors = shownData.map((d) => d.color)

  const _loading = 'loading' in props && props.loading
  const _error = 'error' in props && props.error

  useUnsafeMemo(() => {
    dynamicFiltering(evolutionData, selectedFrequency)
  }, [evolutionData, selectedFrequency])

  return (
    <>
      <Box height="s240" width="full">
        {!_loading && _error && (
          <Box height="s300" width="full">
            <EmptyState
              title={i18n.t('project_management.graphs.missing_data.title')}
              description={i18n.t('project_management.graphs.missing_data.description')}
              icon="monitor-flash"
            />
          </Box>
        )}
        {_loading && <EvolutionGraphLoadingSkeleton />}
        <ResponsiveLine
          data={shownData}
          margin={{ left: 68, top: 10, bottom: 34, right: 50 }}
          xScale={{ type: 'linear' }}
          yScale={{
            type: 'linear',
            min: minY(),
            max: maxY(),
            stacked: false,
            reverse: false,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 14,
            tickRotation: 0,
            tickValues: 4,
            format: (value) => (formatYAxis ? formatYAxis(value) : `${value} ${unit}`),
          }}
          enableGridY
          gridYValues={4}
          yFormat=" >-.2f"
          colors={shownColors.map((c) => colors[c])}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickRotation: 0,
            tickPadding: 14,
            tickValues: evolutionData.data.length || 7,
            renderTick: ({ textAnchor, textX, textY, value, x, y }) => {
              return (
                <g transform={`translate(${x},${y + 8})`}>
                  <text textAnchor={textAnchor} transform={`translate(${textX},${textY + 10})`}>
                    <tspan>{evolutionData.data[value]?.title}</tspan>
                  </text>
                </g>
              )
            },
          }}
          pointSize={7}
          pointBorderWidth={2}
          pointLabelYOffset={-12}
          useMesh
          enableGridX={false}
          enableSlices="x"
          pointSymbol={(point) => (
            <circle
              cx="0"
              cy="0"
              r={
                point.color === colors[evolutionData.color] &&
                point.datum.x !== shownData[0]?.data[0]?.x
                  ? '3'
                  : '0'
              }
              stroke={point.color}
              strokeWidth="2"
              fill={colors.grey000White}
            />
          )}
          theme={{
            grid: {
              line: {
                stroke: '#dddddd',
                strokeWidth: 1,
                strokeDasharray: '4 4',
              },
            },
          }}
          animate={false}
          markers={filteredMarkers}
        />
      </Box>
      <Legend
        items={legendItems}
        legendAction={manageChartVisibility}
        activeLegends={activeSeries}
      />
    </>
  )
}
